import * as React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import HandymanOutlinedIcon from "@mui/icons-material/HandymanOutlined";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";

const rigs = [
  {
    src: "/photos/workshop/ggs120j.jpeg",
    model: "GGS 120J",
    description: "Portable drill rig",
  },
  {
    src: "/photos/workshop/ggs175j.jpeg",
    model: "GGS 175J",
    description: "Portable drill rig",
  },
  {
    src: "/photos/workshop/ggs250j.jpeg",
    model: "GGS 250J",
    description: "Portable drill rig",
  },
];

export default function WorkshopPage() {
  return (
    <Box sx={{ pb: { xs: 7, md: 10 }, backgroundColor: "#f8f7f4" }}>
      <Box
        component="section"
        sx={{
          position: "relative",
          overflow: "hidden",
          color: "white",
          background:
            "linear-gradient(120deg, #081a28 0%, #10364a 68%, #0b5b43 150%)",
          py: { xs: 5, md: 8 },
          "&::before": {
            content: '""',
            position: "absolute",
            width: { xs: 230, md: 440 },
            height: { xs: 230, md: 440 },
            right: { xs: -130, md: "4%" },
            top: { xs: -140, md: -250 },
            border: "1px solid rgba(212, 174, 96, 0.24)",
            borderRadius: "50%",
            boxShadow:
              "0 0 0 35px rgba(212, 174, 96, 0.035), 0 0 0 75px rgba(212, 174, 96, 0.025)",
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                component="p"
                sx={{
                  mb: 1.5,
                  color: "#e1bd70",
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                }}
              >
                Built for the field
              </Typography>
              <Typography
                component="h1"
                sx={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: { xs: 36, sm: 44, md: 52 },
                  fontWeight: 700,
                  lineHeight: 1.15,
                }}
              >
                Our workshop.
                <Box component="span" sx={{ color: "#e1bd70" }}>
                  {" "}Ready for every challenge.
                </Box>
              </Typography>
              <Typography
                sx={{
                  mt: 2,
                  maxWidth: 560,
                  color: "rgba(255,255,255,0.78)",
                  fontSize: { xs: 15, md: 16 },
                  lineHeight: 1.85,
                }}
              >
                Our workshop is where we build, assemble, and maintain the
                equipment behind dependable exploration work. Every rig is
                prepared with care to perform in demanding field conditions.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.25,
                  mt: 3,
                }}
              >
                {[
                  { icon: <HandymanOutlinedIcon />, label: "Built with care" },
                  {
                    icon: <PrecisionManufacturingOutlinedIcon />,
                    label: "Field-ready rigs",
                  },
                ].map((feature) => (
                  <Box
                    key={feature.label}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.5,
                      py: 1,
                      border: "1px solid rgba(255,255,255,0.18)",
                      borderRadius: 5,
                      color: "rgba(255,255,255,0.88)",
                      fontSize: 13,
                    }}
                  >
                    <Box sx={{ display: "flex", color: "#e1bd70" }}>
                      {feature.icon}
                    </Box>
                    {feature.label}
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  p: { xs: 1, sm: 1.5 },
                  border: "1px solid rgba(225,189,112,0.4)",
                  borderRadius: 3,
                  backgroundColor: "rgba(255,255,255,0.08)",
                  boxShadow: "0 20px 50px rgba(0,0,0,0.24)",
                }}
              >
                <Box
                  component="img"
                  src="/photos/services/workshop.jpeg"
                  alt="Drilling equipment being assembled and maintained in the Green Geo Sinergi workshop"
                  loading="lazy"
                  sx={{
                    display: "block",
                    width: "100%",
                    aspectRatio: "4 / 3",
                    objectFit: "cover",
                    borderRadius: 2,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: { xs: 5, md: 8 } }}>
        <Box sx={{ mb: 3.5, textAlign: { xs: "left", md: "center" } }}>
          <Typography
            component="p"
            sx={{
              mb: 1,
              color: "#0b6a4c",
              fontFamily: "'Montserrat', sans-serif",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
            }}
          >
            Our equipment
          </Typography>
          <Typography
            component="h2"
            sx={{
              color: "#081a28",
              fontFamily: "'Montserrat', sans-serif",
              fontSize: { xs: 25, md: 32 },
              fontWeight: 700,
            }}
          >
            Rigs made for the work
          </Typography>
          <Typography
            sx={{
              mt: 1,
              mx: { md: "auto" },
              maxWidth: 600,
              color: "text.secondary",
              fontSize: 14,
              lineHeight: 1.75,
            }}
          >
            Explore our drill rig specifications and download a brochure for
            more details.
          </Typography>
        </Box>

        <Grid container spacing={2.5} alignItems="stretch">
          {rigs.map((rig) => (
            <Grid item xs={12} sm={6} md={4} key={rig.model}>
              <Card
                sx={{
                  height: "100%",
                  overflow: "hidden",
                  border: "1px solid rgba(8, 26, 40, 0.08)",
                  borderRadius: 3,
                  boxShadow: "0 12px 32px rgba(8, 26, 40, 0.08)",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 18px 40px rgba(8, 26, 40, 0.14)",
                  },
                }}
              >
                <Box
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    background:
                      "linear-gradient(145deg, #081a28 0%, #172d3c 100%)",
                  }}
                >
                  <Box
                    component="img"
                    src={rig.src}
                    alt={`${rig.model} ${rig.description} specifications`}
                    loading="lazy"
                    sx={{
                      display: "block",
                      width: "100%",
                      aspectRatio: "3 / 4",
                      objectFit: "contain",
                      borderRadius: 1,
                    }}
                  />
                </Box>
                <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                  <Typography
                    component="p"
                    sx={{
                      mb: 0.5,
                      color: "#0b6a4c",
                      fontFamily: "'Montserrat', sans-serif",
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                    }}
                  >
                    {rig.description}
                  </Typography>
                  <Typography
                    component="h3"
                    sx={{
                      color: "#081a28",
                      fontFamily: "'Montserrat', sans-serif",
                      fontSize: 21,
                      fontWeight: 700,
                    }}
                  >
                    {rig.model}
                  </Typography>
                  <Button
                    component="a"
                    href={rig.src}
                    download
                    fullWidth
                    variant="outlined"
                    endIcon={<DownloadIcon />}
                    sx={{
                      mt: 2.5,
                      py: 1.1,
                      borderColor: "#0b6a4c",
                      borderRadius: 1.5,
                      color: "#0b6a4c",
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      "&:hover": {
                        borderColor: "#081a28",
                        backgroundColor: "rgba(11,106,76,0.06)",
                      },
                    }}
                  >
                    Download brochure
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
